import { Button, EmptyState, Skeleton } from "@hisani/ui";
import { CalendarDays, RefreshCw, TriangleAlert } from "lucide-react";
import { useTranslations } from "next-intl";
import React from "react";

export function PlannerErrorState({
  error,
  onRetry,
  compact = false,
}: {
  readonly error: string;
  readonly onRetry: () => void;
  readonly compact?: boolean;
}) {
  const t = useTranslations("Planner");
  return (
    <div
      role="alert"
      className={`${compact ? "mt-3" : "mt-5"} border-y border-border-subtle py-4`}
    >
      <div className="flex items-start gap-3">
        <TriangleAlert className="mt-0.5 size-5 shrink-0" aria-hidden="true" />
        <div className="min-w-0 flex-1">
          <p className="text-base font-bold text-primary">{t("errorTitle")}</p>
          <p className="mt-1 text-sm leading-5 text-secondary">{error}</p>
        </div>
      </div>
      <Button
        type="button"
        onClick={onRetry}
        variant="secondary"
        size="lg"
        className="mt-3 min-h-12 w-full gap-2"
      >
        <RefreshCw className="size-4" aria-hidden="true" />
        {t("retry")}
      </Button>
    </div>
  );
}

export function PlannerLoadingState() {
  const t = useTranslations("Planner");
  return (
    <div
      className="mt-3 divide-y divide-border-subtle border-y border-border-subtle bg-surface"
      aria-label={t("loading")}
      role="status"
    >
      {[0, 1].map((item) => (
        <div key={item} className="px-1 py-3 sm:px-3">
          <div className="flex justify-between gap-4">
            <Skeleton className="h-4 w-32" />
            <Skeleton className="h-5 w-16 rounded-full" />
          </div>
          <Skeleton className="mt-2 h-5 w-24" />
          <Skeleton className="mt-2 h-4 w-40" />
          <div className="mt-2 grid grid-cols-2 gap-x-4 gap-y-2">
            <Skeleton className="h-7" />
            <Skeleton className="h-7" />
            <Skeleton className="h-7" />
            <Skeleton className="h-7" />
          </div>
          <Skeleton className="mt-2 h-11" />
        </div>
      ))}
      <span className="sr-only">{t("loading")}</span>
    </div>
  );
}

export function PlannerEmptyState({
  savedView,
}: {
  readonly savedView: boolean;
}) {
  const t = useTranslations("Planner");
  return (
    <EmptyState
      icon={CalendarDays}
      title={savedView ? t("savedEmptyTitle") : t("emptyTitle")}
      description={
        savedView ? t("savedEmptyDescription") : t("emptyDescription")
      }
      className="mt-4 py-10"
    />
  );
}
